import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./users.css";

import { ColumnDef, Row, SortingState, createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtual } from "react-virtual";

// UI Imports
import Skeleton from "../../components/skeleton/Skeleton";
import HeaderUnit from "../../components/header/HeaderUnit";
import GenericCell from "../../components/cell/GenericCell";
import BatteryCell from "../../components/cell/battery/BatteryCell";
import LastSyncTimeCell from "../../components/cell/lastSyncedTime/LastSyncTimeCell";
import LoginStatusCell from "../../components/cell/loginStatus/loginStatusCell";

import ActionCell from "../../components/cell/action/ActionCell";
import copyClipboardIcon from "../../assets/Indicator.svg";

import {convertTimeString} from "../../utils/TimeUtils"


import MasterButton, { ButtonLayoutType, ButtonSize, ButtonVariation } from "../../components/button/MasterButton";
import  SeeMoreIcon  from "../../assets/SeeMoreIcon.png";

import SeeMoreCell from "../../components/cell/action/SeeMoreCell";

import ModalOverlay from "../../components/modal/ModalOverlay";

import UserInfoModal from "./usersDetail/userInfoModal";

// Define the type for user data, including userId
interface UserDetails {
  userId: string;
  name: string;
  email: string;
  role: string;
  lastLogin: string;
  status: string;
  invitedOn: string;
  organizationName: string; // Example: 'org-A'
  studyName: string; // Example: 'study-A'
  loginIcon: string;
}

type TableOptions = {
  tabName: string;
  isStudySelected: boolean;
  isStudyOrgSelected: boolean;
  networkError: boolean;
  isLoading: boolean;
}

// Define props for the UserTable component
interface UserTableProps {
  UsersTableDataArray: UserDetails[];
  TableOptions: TableOptions;
}

const UserTable: React.FC<UserTableProps> = ({ UsersTableDataArray, TableOptions }) => {

  const [isLoading, setLoading] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isTableHorizontallyScrolled, setTableHorizontallyScrolled] = useState(false);


  const columnHelper = createColumnHelper<UserDetails>();
  const [data, setData] = useState<UserDetails[]>([]);

  const [isModalVisible, setModalVisible] = useState(false);

  const [selectedParticipant, setSelectedParticipant] = useState<number>();

  useEffect(() => {
    setData([...UsersTableDataArray]);
  }, [UsersTableDataArray]);

  useEffect(() => {
    setLoading(false)
  }, [TableOptions.isLoading])

  const columns = useMemo<ColumnDef<UserDetails, any>[]>(
    () => [

      columnHelper.accessor(row => row.name, {
        id: "Name",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Name"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),

      columnHelper.accessor(row => row.email, {
        id: "email",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Email Address"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),
      
      columnHelper.accessor(row => row.role, {
        id: "role",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Role"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),

      ...(TableOptions.isStudySelected === false ? [columnHelper.accessor(row => row.studyName, {
        id: "UserStudy",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Study ID"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),  
        size: 208,
      })] : []),

      ...(TableOptions.isStudyOrgSelected === false ? [ columnHelper.accessor(row => row.organizationName, {
        id: "UserOrganization",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={info.getValue()}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Organization Name"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      })] : []),

      columnHelper.accessor(row => row.lastLogin, {
        id: "lastLogin",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={convertTimeString(info.getValue())}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Last Login"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),

      columnHelper.accessor(row => {
        return { loginStatus: row.status, loginIcon: row.loginIcon };
        }, {
        id: "status",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <LoginStatusCell value={info.getValue().loginStatus} loginStatus={info.getValue().loginIcon}></LoginStatusCell>),
        header: (header) => (
        <HeaderUnit
            value={"Status"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),

      columnHelper.accessor(row => row.invitedOn, {
        id: "invitedOn",
        cell: (info) => (isLoading ? <Skeleton></Skeleton> : <GenericCell value={convertTimeString(info.getValue())}></GenericCell>),
        header: (header) => (
        <HeaderUnit
            value={"Invited On"}
            isSorted={header.column.getIsSorted()}
            onClicked={function (): void {
            header.column.toggleSorting();
            }}
        ></HeaderUnit>
        ),
        size: 208,
      }),

      columnHelper.accessor(row => row, {
        id: " ",
        cell: (info) => (isLoading 
            ? <Skeleton></Skeleton> 
            : <SeeMoreCell
                onClicked={() => {
                  setSelectedParticipant(3);
                  setModalVisible(true);
                }}
              ></SeeMoreCell>),
        
        size: 120,
      })

    ], [columnHelper, isLoading]);

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      enableSorting: true,
      debugTable: true,
    })

    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtual({
        parentRef: scrollRef,
        size: rows.length,
        overscan: 10,
    });

    const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;


  return (
    <>
        <div
          className="data-table"
          ref={scrollRef}
          onScroll={() => {
            if (scrollRef.current?.scrollLeft !== 0) {
              setTableHorizontallyScrolled(true);
            } else {
              setTableHorizontallyScrolled(false);
            }
          }}
        >
          {data.length !== 0 ? (
            <table
              style={{
                width: table.getCenterTotalSize(),
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th className={isTableHorizontallyScrolled && header.id === "Name" ? "header-item drop-shadow" : "header-item"} key={header.id} style={{ width: header.getSize() }}>
                        {header.isPlaceholder ? null : <>{flexRender(header.column.columnDef.header, header.getContext())}</>}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px` }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index] as Row<UserDetails>;

                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td className={isTableHorizontallyScrolled && cell.id.includes("Name") ? "drop-shadow" : ""} key={cell.id}>                          
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="no-data body-large">
                Loading data...
               {/* There are no devices in this category. */}
               {/* Add Error Status here */}
            </div>
          )}
        </div>
      
      {isModalVisible && (
        <ModalOverlay
          closeModal={() => {
            setModalVisible(false);
          }}
        >
          <UserInfoModal
            index={selectedParticipant ? selectedParticipant : -1}
            onCloseClicked={() => {    
              setModalVisible(false);
            }}
          ></UserInfoModal>
        </ModalOverlay>
      )}


    </>
  );
};

export default UserTable;
